import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { musicOnHoldApi } from '~/apis/musicOnHoldApi'
import { useUserState } from '~/atoms/UserState'
import { toaster } from '~/components/ui/toaster'
import { ApplicationRoutes } from '~/hooks/useRouting'

import {
  ManageAudioTracksModal,
  MusicOnHoldListPage,
  PlaylistDetailPage,
} from '@mangovoice/music-on-hold-ui'
import type { PlaylistListItem } from '@mangovoice/music-on-hold-ui'

interface MusicOnHoldSettingsPageProps {
  playlistId?: string
}

export const MusicOnHoldSettingsPage = ({
  playlistId,
}: MusicOnHoldSettingsPageProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { pbxId } = useUserState({ canAutoRefresh: false })

  const [audioTracksOpen, setAudioTracksOpen] = useState(false)

  const handleError = useCallback(
    (error: Error) => {
      toaster.create({
        title: t('musicOnHold.error', 'Error'),
        description: error.message,
        type: 'error',
        duration: 5000,
        closable: true,
      })
    },
    [t],
  )

  if (!pbxId) {
    return null
  }

  const handleRowClick = (playlist: PlaylistListItem) => {
    navigate(`${ApplicationRoutes.SETTINGS}/music-on-hold/${playlist.id}`)
  }

  const listPath = `${ApplicationRoutes.SETTINGS}/music-on-hold`

  if (playlistId) {
    return (
      <PlaylistDetailPage
        api={musicOnHoldApi}
        pbxId={pbxId}
        playlistId={Number(playlistId)}
        listPath={listPath}
        toast={toaster}
        t={t}
      />
    )
  }

  return (
    <>
      <MusicOnHoldListPage
        api={musicOnHoldApi}
        pbxId={pbxId}
        onRowClick={handleRowClick}
        onManageAudioTracks={() => setAudioTracksOpen(true)}
        onError={handleError}
        t={t}
      />
      <ManageAudioTracksModal
        api={musicOnHoldApi}
        pbxId={pbxId}
        open={audioTracksOpen}
        onClose={() => setAudioTracksOpen(false)}
        toast={toaster}
        t={t}
      />
    </>
  )
}
